// src/pages/MyPage/MyTicket.jsx

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

import { sendToApp, addAppMessageListener } from "../../../bridges/appBridge";
// import useAuth from "../../../hooks/Regist/useAuth";
// import AuthDebugPanelInline from "../../AdsLogin/components/detailComponents/AuthDebugPanelInline";
import check from "../../../assets/icons/Select/check_checked.svg";
import { HeaderIconL } from "../../../assets/components/HeaderGroup";
import home from "../../../assets/icons/Arrow/left.svg";
import { useFixedLayout } from "../../../hooks/useFixedLayout";
import radioOn from "../../../assets/icons/Select/radio_checked_color.svg"
import radioOff from "../../../assets/icons/Select/radio_unchecked.svg"
import PaymentHistory from "./MyPaymentHistory";
import TokenBizInfoPage from "../../../assets/components/TokenBizInfoPage";
import { useToast } from "../../../hooks/useToast";

const API = process.env.REACT_APP_FASTAPI_ADS_URL;

// 앱 메시지 기다리는 헬퍼
function waitAppMessage(filterFn, timeoutMs = 8000) {
    return new Promise((resolve, reject) => {
        const off = addAppMessageListener((msg) => {
            try {
                if (filterFn(msg)) {
                    clearTimeout(timer);
                    off?.();
                    resolve(msg);
                }
            } catch {
                // 무시
            }
        });

        const timer = setTimeout(() => {
            off?.();
            reject(new Error(`waitAppMessage timeout ${timeoutMs}ms`));
        }, timeoutMs);
    });
}

// 숫자 → "5,500" 포맷
const fmt = (n) => (Number(n) || 0).toLocaleString("ko-KR");

const MyTicket = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const initialTab = location.state?.tab || "token";
    const [activeTab, setActiveTab] = useState(initialTab);
    const { headerRef, headerSpace } = useFixedLayout();

    const [planList, setPlanList] = useState([]);
    const [purchaseList, setPurchaseList] = useState(null);
    const [selectedTicketId, setSelectedTicketId] = useState(null);

    const [isPurchasing, setIsPurchasing] = useState(false);

    const { showToast } = useToast();
    // const { session, isAuthed, loading, error } = useAuth();

    // 서버로 보낼 마지막 결제 payload
    const lastPurchasePayloadRef = useRef(null);

    // 이번 결제 시도에 대해 결과를 이미 처리했는지 여부
    const purchaseHandledRef = useRef(false);

    // // 상단 state들 아래에 추가
    // const [sortOrder, setSortOrder] = useState("desc"); // "asc" | "desc"

    // const toggleSortOrder = () => {
    //     setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
    // };

    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: "auto",
        });
    }, [activeTab]);

    // ===== 웹뷰 쪽에 "IAP 준비됨" 알림 =====
    useEffect(() => {
        const sendReady = () => {
            window.ReactNativeWebView?.postMessage(
                JSON.stringify({ type: "WEB_IAP_READY" })
            );
        };
        sendReady();

        window.addEventListener("pageshow", sendReady);
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible") sendReady();
        });

        return () => {
            window.removeEventListener("pageshow", sendReady);
        };
    }, []);

    // ===== 상품 목록 조회 (/plan/list) =====
    const fetchPlans = async () => {
        try {
            const res = await axios.get(`${API}/plan/list`);
            setPlanList(res.data || []);
        } catch (err) {
            console.error("이용권 목록 조회 실패", err);
        }
    };

    // ===== 결제 내역 조회 (/ticket/list) =====
    const fetchPaymentList = async () => {
        try {
            const user_id = localStorage.getItem("user_id");
            if (!user_id) return;

            const res = await axios.get(`${API}/ticket/list`, {
                params: { user_id },
            });

            const payload = res.data ?? [];

            const purchase = Array.isArray(payload)
                ? payload
                : (Array.isArray(payload.purchase) ? payload.purchase : []);

            setPurchaseList(purchase)
        } catch (err) {
            console.error("결제 내역 조회 실패", err);
            setPurchaseList([])
        }
    };

    useEffect(() => {
        fetchPlans();
        fetchPaymentList();
    }, []);

    // ===== 토큰(단건) 전용 플랜만 추리기 =====
    const tokenPlans = useMemo(
        () =>
            (Array.isArray(planList) ? planList : []).filter((p) => {
                const cycle = Number(p.BILLING_CYCLE);
                const type = String(p.TICKET_TYPE || "").toLowerCase();
                // BILLING_CYCLE = 0 이고, 테스트/컨시어지 등은 제외
                if (cycle !== 0) return false;
                if (["test", "start", "concierge"].includes(type)) return false;
                return true;
            }),
        [planList]
    );

    // 현재 선택된 플랜(단건 1개)
    const selectedPlan = useMemo(
        () => tokenPlans.find((p) => p.TICKET_ID === selectedTicketId) || null,
        [tokenPlans, selectedTicketId]
    );

    // 앱 쪽에 IAP 정보 요청
    async function requestIapInfoSimple(timeoutMs = 8000) {
        sendToApp("GET_IAPINFO");
        const msg = await waitAppMessage(
            (m) => m?.type === "IAPINFO",
            timeoutMs
        );
        return msg.payload ?? msg;
    }

    // 상품 → 인앱 productId 매핑 (필요하면 여기만 수정)
    function mapPlanToProductId(plan) {
        const t = String(plan.planType || "").toLowerCase();
        const c = plan.ticket_id;

        const table = {
            basic: { 18: "wm_basic_n", 19: "wm_standard_n", 20: "wm_premium_n" },
        };

        const productId = table[t]?.[c];
        if (!productId) throw new Error(`매핑 누락: type=${t}, cycle=${c}`);
        return productId;
    }

    // ===== 앱에서 결제 결과 받기 =====
    useEffect(() => {
        const off = addAppMessageListener(async (msg) => {
            console.log("[APP_MSG]", msg);

            if (
                msg?.type !== "PURCHASE_RESULT" &&
                msg?.type !== "SUBSCRIPTION_RESULT"
            ) {
                return;
            }

            if (purchaseHandledRef.current) {
                // 이미 이번 결제에 대한 결과를 한 번 처리했다면 무시
                return;
            }

            const p = msg.payload ?? msg;
            const base = lastPurchasePayloadRef.current;

            // 0) 사용자 결제창 취소 → BE 전송 X
            if (!p.success && (p.cancelled || p.error_code === "E_USER_CANCELLED")) {
                setIsPurchasing(false);
                lastPurchasePayloadRef.current = null;
                setSelectedTicketId(null);
                return;
            }

            // 공통적으로 필요한 컨텍스트가 없으면 실패 처리
            if (!base) {
                alert("상품 정보를 받지 못함")
                setIsPurchasing(false);
                return;
            }

            // 성공/실패 공통으로 묶을 필드
            const commonBody = {
                ...base,
                platform: p.platform || "android",
                product_id: p.product_id || base.product_id,
                purchase_token: p.purchase_token || null,
                transaction_id: p.transaction_id || null,
            };

            // 1) 결제 실패(취소 아님) → type = "fail" 로 BE에 로그 남기기
            if (!p.success) {
                // 이 시점부터는 이번 결제 결과는 처리된 것으로 간주
                purchaseHandledRef.current = true;

                const failBody = {
                    ...commonBody,
                    type: "fail",
                };

                try {
                    await axios.post(`${API}/token/purchase`, failBody, {
                        headers: { "Content-Type": "application/json" },
                    });

                } catch (err) {
                } finally {
                    await fetchPaymentList();

                    showToast("결제에 실패했습니다", {
                        placement: "bottom",
                        bottomOffset: "8px",
                    });
                    setIsPurchasing(false);
                    lastPurchasePayloadRef.current = null;
                    setSelectedTicketId(null);
                }
                return;
            }

            // 2) 결제 성공 → 기존처럼 type = "one_time" 그대로 사용
            purchaseHandledRef.current = true;

            const successBody = {
                ...commonBody,
                type: base.type ?? "one_time",
            };

            try {
                const res = await axios.post(`${API}/token/purchase`, successBody, {
                    headers: { "Content-Type": "application/json" },
                });
                console.log("[IAP] success body", successBody);

                if (res.data?.success) {
                    await fetchPaymentList();
                    setActiveTab("history");
                    showToast("결제를 완료했습니다", {
                        placement: "bottom",
                        bottomOffset: "8px",
                    });

                } else {
                    alert(
                        "서버 검증 실패: " +
                        (res.data?.message || "알 수 없는 오류입니다.")
                    );
                }
            } catch (err) {
                console.error("결제 서버 오류:", err);
                alert(
                    "결제 서버 오류: " +
                    (err || "알 수 없는 오류입니다.")
                );
            } finally {
                setIsPurchasing(false);
                lastPurchasePayloadRef.current = null;
                setSelectedTicketId(null);
            }
        });

        return () => off?.();
    }, [showToast]);


    // ===== 결제 버튼 클릭 =====
    // ===== 요금제 선택 시 바로 결제 =====
    const onPurchase = async (targetPlan) => {
        const plan = targetPlan || selectedPlan;

        if (!plan) {
            alert("구매할 토큰 상품을 선택해주세요.");
            return;
        }

        const user_id = localStorage.getItem("user_id");
        if (!user_id) {
            alert("로그인 후 이용해주세요.");
            return;
        }

        const now = new Date();

        const planType = String(plan.TICKET_TYPE || "").toLowerCase();
        const ticket_id = plan.TICKET_ID;

        let productId;
        try {
            productId = mapPlanToProductId({ planType, ticket_id });
        } catch (e) {
            console.error(e);
            alert("인앱 상품 ID 매핑에 실패했습니다. (관리자에게 문의)");
            return;
        }

        // 서버에 보낼 원본 payload 저장
        lastPurchasePayloadRef.current = {
            user_id,
            ticket_id,
            payment_method: "app",
            payment_date: now.toISOString(),
            qty: 1,
            type: "one_time",
            billing_cycle: "없음",
            plan_type: planType,
            product_id: productId,
        };

        purchaseHandledRef.current = false;

        console.log("[PURCHASE_PAYLOAD]", lastPurchasePayloadRef.current);

        setIsPurchasing(true);

        // 앱 쪽 인앱 결제 시작
        sendToApp("START_ONE_TIME_PURCHASE", { product_id: productId });

        try {
            const info = await requestIapInfoSimple(10000);
            console.log("[IAPINFO]", info);
        } catch (e) {
            console.warn("IAPINFO timeout/error:", e);
        }
    };


    const navMyPage = () => {
        navigate("/ads/main", { state: { tab: "mypage" } });
    };

    // const onClickBuyToken = () => {
    //     navigate("/myticket", { state: { tab: "token" } });
    // };

    return (
        <div className="mx-auto h-dvh flex-col bg-white">
            <div ref={headerRef} className="fixed top-0 left-0 right-0 z-10">
                <HeaderIconL
                    className=""
                    title="토큰 구매"
                    icon={home}
                    onIconClick={navMyPage}
                />

                <div className="flex border-b border-border-subtle bg-surface-light-white">
                    <button
                        type="button"
                        onClick={() => setActiveTab("token")}
                        className={`
                            relative flex-1 pb-[10px] text-t4 text-center
                        ${activeTab === "token"
                                ? "text-text-primary"
                                : "text-text-subtler"
                            }
                        `}
                    >
                        토큰 구매
                        {activeTab === "token" && (
                            <span className="absolute bottom-0 left-0 w-full border-[1px] border-border-primary" />
                        )}
                    </button>

                    <button
                        type="button"
                        onClick={() => setActiveTab("history")}
                        className={`
                            relative flex-1 pb-[10px] text-t4 text-center
                        ${activeTab === "history"
                                ? "text-text-primary"
                                : "text-text-subtler"
                            }
                        `}
                    >
                        결제 내역
                        {activeTab === "history" && (
                            <span className="absolute bottom-0 left-0 w-full border-[1px] border-border-primary" />
                        )}
                    </button>
                </div>
            </div>

            <div
                className="flex-1 flex flex-col"
                style={{ paddingTop: headerSpace }}
            >
                {activeTab === "token" && (
                    <>
                        <main
                            className="flex-1 overflow-y-auto"
                        >
                            {/* 상단 카피 + 기능 리스트 */}
                            <section>
                                <p className="text-t2 text-text-default pt-[16px] pl-[16px]">
                                    토큰을 구매하고
                                    <br />더 많은 마케팅을 시작해 보세요
                                </p>

                                <ul className="pt-[40px] text-b1 text-text-default px-[8px]">
                                    <li className="flex items-start gap-[4px] p-[8px]">
                                        <img src={check} alt="" />
                                        <span>가게 분석 AI Report 제공</span>
                                    </li>
                                    <li className="flex items-start gap-[4px] p-[8px]">
                                        <img src={check} alt="" />
                                        <span>50여 종의 디자인 스타일</span>
                                    </li>
                                    <li className="flex items-start gap-[4px] p-[8px]">
                                        <img src={check} alt="" />
                                        <span>20여 종의 디자인 템플릿</span>
                                    </li>
                                    <li className="flex items-start gap-[4px] p-[8px]">
                                        <img src={check} alt="" />
                                        <span>SNS 마케팅 효과</span>
                                    </li>
                                </ul>
                            </section>

                            {/* 토큰 상품 리스트 */}
                            <section className="pt-[24px]">
                                {tokenPlans.map((plan) => {
                                    const selected = plan.TICKET_ID === selectedTicketId;
                                    return (
                                        <div key={plan.TICKET_ID} className="w-full px-[16px] pb-[12px]">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (isPurchasing) return;         // 결제 중이면 중복 클릭 막기
                                                    setSelectedTicketId(plan.TICKET_ID);
                                                    onPurchase(plan);                 // 선택과 동시에 결제 시작
                                                }}
                                                // onClick={() => {
                                                //     const planType = String(plan.TICKET_TYPE || "").toLowerCase();
                                                //     console.log("[MyTicket] 선택한 요금제:", plan);
                                                //     console.log("[MyTicket] planType =", planType);
                                                //     setSelectedTicketId(plan.TICKET_ID);
                                                // }}
                                                className={`flex w-full justify-between p-[16px] border rounded-[16px] ${selected ? "border-border-primary" : "border-border-subtle"
                                                    } ${isPurchasing ? "opacity-60 pointer-events-none" : ""}`}
                                            >

                                                <div className="flex items-center gap-[8px]">
                                                    <span className="flex items-center justify-center">
                                                        <img
                                                            src={selected ? radioOn : radioOff}
                                                            alt={selected ? "선택됨" : "미선택"}
                                                        />
                                                    </span>

                                                    <span className="pl-[16px] text-b1 text-text-subtle whitespace-nowrap">
                                                        토큰 {plan.TOKEN_AMOUNT}개
                                                    </span>
                                                </div>
                                                <span className="text-t4 text-text-subtle text-right">
                                                    {fmt(plan.TICKET_PRICE)}원
                                                </span>
                                            </button>
                                        </div>
                                    );
                                })}

                                {tokenPlans.length === 0 && (
                                    <p className="py-6 text-center text-xs text-[#8C96A8]">
                                        구매 가능한 토큰 상품이 없습니다.
                                    </p>
                                )}
                            </section>

                            <div className="pt-[8px] mx-[16px] border-b-[1px] border-border-subtle" />

                            <div className="pb-[32px]">
                                <TokenBizInfoPage />
                            </div>

                            <div className="mt-6">
                                {/* <AuthDebugPanelInline
                                    session={session}
                                    isAuthed={isAuthed}
                                    loading={loading}
                                    error={error}
                                /> */}
                            </div>
                        </main>

                    </>
                )}

                {activeTab === "history" && (
                    <PaymentHistory
                        purchased={purchaseList}
                        // sortOrder={sortOrder}
                        // toggleSortOrder={toggleSortOrder}
                        onClickBuyToken={() => setActiveTab("token")}
                    />
                )}
            </div>
        </div>
    );
};

export default MyTicket;
